<template>
    <div class="section">
        <div class="center-section">
            <div class="form-heading">
                <h1 class="header-1">{{ $t('auth.signup.heading') }}</h1>
                <img src="@/assets/fingers.png" :alt="$t('app.logoAlt')" />
            </div>

            <div class="signup-form">
                <div v-if="errorMessage" class="error-message">
                    {{ errorMessage }}
                    <router-link v-if="errorDetails" to="/login" class="text-link">
                        {{ errorDetails }}
                    </router-link>
                </div>

                <div v-if="successMessage" class="success-message">
                    {{ successMessage }}
                </div>

                <p class="form-description">
                    {{ $t('auth.signup.description') }}
                </p>

                <form @submit.prevent="handleSubmit">
                    <base-input 
                        class="form-group" 
                        type="text" 
                        :placeholder="$t('auth.signup.fullName')"
                        required 
                        icon="person" 
                        v-model="name"
                        :error="nameError"
                    >
                        <template #hint>
                            <span class="input-hint">{{ $t('auth.signup.fullNameHint') }}</span>
                        </template>
                    </base-input>

                    <base-input 
                        class="form-group" 
                        type="email" 
                        :placeholder="$t('auth.signup.emailAddress')"
                        required 
                        icon="envelop"
                        v-model="email" 
                        :error="emailError"
                    >
                        <template #hint>
                            <span class="input-hint">{{ $t('auth.signup.emailHint') }}</span>
                        </template>
                    </base-input>

                    <div class="password-section">
                        <base-input 
                            class="form-group" 
                            type="password" 
                            :placeholder="$t('auth.signup.createPassword')"
                            required 
                            icon="lock"
                            v-model="password" 
                            :error="passwordError"
                        />
                        
                        <div class="password-strength" v-if="password">
                            <div class="strength-item" :class="{ active: hasMinLength }">
                                <BaseIcon iconName="check" v-if="hasMinLength" />
                                <BaseIcon iconName="circle" v-else />
                                {{ $t('auth.passwordRules.minLength') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasUpperCase }">
                                <BaseIcon iconName="check" v-if="hasUpperCase" />
                                <BaseIcon iconName="circle" v-else />
                                {{ $t('auth.passwordRules.uppercase') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasLowerCase }">
                                <BaseIcon iconName="check" v-if="hasLowerCase" />
                                <BaseIcon iconName="circle" v-else />
                                {{ $t('auth.passwordRules.lowercase') }}
                            </div>
                            <div class="strength-item" :class="{ active: hasNumber }">
                                <BaseIcon iconName="check" v-if="hasNumber" />
                                <BaseIcon iconName="circle" v-else />
                                {{ $t('auth.passwordRules.number') }}
                            </div>
                        </div>

                        <base-input 
                            class="form-group" 
                            type="password" 
                            :placeholder="$t('auth.signup.confirmPassword')"
                            required 
                            icon="lock"
                            v-model="confirmPassword" 
                            :error="confirmError"
                        />
                    </div>

                    <primary-btn 
                        :disabled="!isValid" 
                        :loading="isLoading"
                    >
                        {{ isLoading ? $t('auth.signup.creating') : $t('auth.signup.createAccount') }}
                    </primary-btn>
                </form>
                <div class="or-divider">
                    <span class="or-divider-line"></span>
                    <span class="or-divider-text caption">{{ $t('auth.or') }}</span>
                    <span class="or-divider-line"></span>
                </div>
                <GoogleSignUp />
                <i18n-t keypath="auth.signup.recaptcha" tag="p" class="recaptcha-disclaimer">
                    <template #privacy>
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">{{ $t('auth.signup.privacyPolicy') }}</a>
                    </template>
                    <template #terms>
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">{{ $t('auth.signup.termsOfService') }}</a>
                    </template>
                </i18n-t>
            </div>

            <div class="login-link caption">
                {{ $t('auth.signup.existingAccount') }} <router-link to="/login" class="link">{{ $t('auth.signup.logIn') }}</router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue';
import PrimaryBtn from '@/components/BaseComponents/PrimaryBtn.vue';
import GoogleSignUp from '@/components/AuthProviders/GoogleSignUp.vue';
import { authAPI } from '@/services/api';
import { trackEvent, trackFormSubmission, trackError } from '@/utils/analytics';
import { useI18n } from 'vue-i18n';

const name = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const errorDetails = ref('');
const successMessage = ref('');

const router = useRouter();
const { t } = useI18n();

const isLoading = ref(false);

// Password strength indicators
const hasMinLength = computed(() => password.value.length >= 8);
const hasUpperCase = computed(() => /[A-Z]/.test(password.value));
const hasLowerCase = computed(() => /[a-z]/.test(password.value));
const hasNumber = computed(() => /[0-9]/.test(password.value));

const nameError = computed(() => {
    if (!name.value.trim()) return '';
    if (name.value.trim().length < 2) return t('auth.signup.nameError');
    return '';
});

const emailError = computed(() => {
    if (!email.value.trim()) return '';
    if (!email.value.includes('@') || !email.value.includes('.')) {
        return t('auth.signup.emailError');
    }
    return '';
});

const passwordError = computed(() => {
    if (!password.value) return '';
    if (password.value.length < 8) return t('auth.signup.passwordError');
    return '';
});

const confirmError = computed(() => {
    if (!confirmPassword.value) return '';
    if (confirmPassword.value !== password.value) return t('auth.signup.passwordsMismatch');
    return '';
});

const isValid = computed(() => {
    return name.value.trim() !== '' &&
        email.value.trim() !== '' &&
        email.value.includes('@') &&
        password.value.length >= 8 &&
        confirmPassword.value === password.value;
});

async function handleSubmit() {
    if (!isValid.value) {
        errorMessage.value = t('auth.signup.completeFields');
        return;
    }

    isLoading.value = true;
    errorMessage.value = '';
    errorDetails.value = '';
    successMessage.value = '';

    trackEvent('signup_started', {
        has_name: !!name.value,
        email_domain: email.value.split('@')[1] || 'unknown',
        password_strength: {
            min_length: hasMinLength.value,
            has_upper: hasUpperCase.value,
            has_lower: hasLowerCase.value,
            has_number: hasNumber.value
        }
    });

    try {
        // Execute reCAPTCHA v3
        const recaptchaToken = await window.grecaptcha.execute(
            process.env.VUE_APP_RECAPTCHA_SITE_KEY,
            { action: 'signup' }
        );

        // Use authAPI.register with recaptcha token
        await authAPI.register({
            name: name.value,
            email: email.value,
            password: password.value,
            recaptcha_token: recaptchaToken
        });

        trackEvent('signup_success', {
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('signup_form', true);

        successMessage.value = t('auth.signup.created');

        setTimeout(() => {
            router.push({
                path: '/signup/success',
                query: { email: email.value }
            });
        }, 1500);

    } catch (error) {
        console.error('Error during registration:', error);

        // Handle error from authAPI
        const errorDetail = error.data?.detail || error.message || '';
        let errorType = 'registration_failed';

        if (errorDetail.toLowerCase().includes('email')) {
            errorMessage.value = t('auth.signup.invalidEmail');
            errorType = 'invalid_email';
        } else if (errorDetail.toLowerCase().includes('exists')) {
            errorMessage.value = t('auth.signup.emailExists');
            errorDetails.value = t('auth.signup.loginLink');
            errorType = 'email_exists';
        } else {
            errorMessage.value = t('auth.signup.failed');
        }

        trackEvent('signup_failed', {
            error_type: errorType,
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('signup_form', false);
        trackError('signup_error', errorDetail || 'Unknown error', 'signup_form');
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.section {
    display: flex;
    align-items: center;
}

.center-section {
    width: 30%;
    margin: auto;
    max-width: 500px;
}

.form-heading {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
}

.form-heading h1 {
    margin-bottom: var(--spacing-md);
}

.signup-form {
    padding: var(--spacing-2xl);
    margin-bottom: var(--spacing-lg);
    background-color: var(--bg-card);
    border-radius: var(--border-radius-2xl);
}

.login-link {
    display: block;
    text-align: center;
    color: var(--color-white-40);
    margin-top: var(--spacing-lg);
}

.link {
    color: var(--color-white);
    text-decoration: none;
    font-weight: var(--font-weight-medium);
}

.link:hover {
    text-decoration: underline;
}

.error-message {
    background-color: var(--color-cancel-15);
    color: var(--color-error-light);
    text-align: center;
    margin-bottom: var(--spacing-xl);
    padding: var(--spacing-sm);
    border-radius: var(--border-radius-xs);
}

.success-message {
    background-color: var(--color-accent-primary-15);
    color: var(--color-accent-primary);
    text-align: center;
    margin-bottom: var(--spacing-xl);
    padding: var(--spacing-sm);
    border-radius: var(--border-radius-xs);
}

.form-description {
    text-align: center;
    color: var(--color-white-70);
    margin-bottom: var(--spacing-lg);
}

.input-hint {
    font-size: var(--font-size-description);
    color: var(--color-white-60);
    margin-top: var(--spacing-xs);
}

.password-section {
    margin-bottom: var(--spacing-lg);
}

.password-strength {
    background: var(--color-white-05);
    border-radius: var(--border-radius-md);
    padding: var(--spacing-md);
    margin: var(--spacing-sm) 0;
}

.strength-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    color: var(--color-white-60);
    font-size: var(--font-size-description);
    margin: var(--spacing-xs) 0;
}

.strength-item.active {
    color: var(--color-accent-primary);
}

.text-link {
    color: var(--color-accent-primary);
    text-decoration: none;
    font-weight: var(--font-weight-medium);
    transition: opacity var(--transition-fast);
}

.text-link:hover {
    opacity: 0.8;
}

.or-divider {
    display: flex;
    align-items: center;
    margin: var(--spacing-xl) 0;
}

.or-divider-line {
    flex: 1;
    height: 1px;
    background-color: var(--color-white-10);
}

.or-divider-text {
    padding: 0 var(--spacing-md);
    color: var(--color-white-40);
}

.recaptcha-disclaimer {
    text-align: center;
    font-size: var(--font-size-description);
    color: var(--color-white-40);
    margin-top: var(--spacing-lg);
    line-height: 1.5;
}

.recaptcha-disclaimer a {
    color: var(--color-white-60);
    text-decoration: none;
    transition: color var(--transition-fast);
}

.recaptcha-disclaimer a:hover {
    color: var(--color-white);
}

@media (max-height: 900px) {
    .form-description {
        display: none;
    }

    .signup-form {
        padding: var(--spacing-xl);
    }

    .or-divider {
        margin: var(--spacing-lg) 0;
    }

    .form-heading h1 {
        margin-bottom: 0;
    }
}

@media (max-width: 1000px) {
    .center-section {
        width: 90%;
        max-width: none;
    }

    .signup-form {
        padding: var(--spacing-xl);
    }

    .form-group {
        padding: var(--spacing-md);
    }
}

@media (max-width: 768px) {
    .form-heading h1 {
        font-size: var(--font-size-h2);
        margin-bottom: 0;
    }

    .form-description {
        display: none;
    }

    .signup-form {
        padding: var(--spacing-lg);
    }

    .password-section {
        margin-bottom: var(--spacing-sm);
    }

    .or-divider {
        margin: var(--spacing-md) 0;
    }

    .recaptcha-disclaimer {
        margin-top: var(--spacing-md);
    }

    .login-link {
        margin-top: var(--spacing-md);
    }
}
</style>
